import { kitchenTicketsService } from '@/services/kitchen/kitchenTicketsService';
import type { KitchenTicket } from '@/types/kitchen';

/**
 * Ticket IDs removed from the board by a local optimistic update. Realtime
 * refetch uses the same queryFn, so this map is the only way to keep a
 * just-delivered card from flashing back before the server write lands.
 *
 * Entries self-expire after DISMISS_TTL_MS instead of living forever: the
 * race this guards against (a refetch racing the write) only matters for a
 * few seconds, and without a TTL a long-running shift would grow this map by
 * one entry per delivered/cancelled ticket for the life of the tab.
 */
const DISMISS_TTL_MS = 60_000;
const dismissedTicketTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const kitchenBoardCache = {
  dismiss(ticketId: string) {
    const existing = dismissedTicketTimeouts.get(ticketId);
    if (existing) clearTimeout(existing);
    const timeout = setTimeout(() => {
      dismissedTicketTimeouts.delete(ticketId);
    }, DISMISS_TTL_MS);
    dismissedTicketTimeouts.set(ticketId, timeout);
  },
  restore(ticketId: string) {
    const existing = dismissedTicketTimeouts.get(ticketId);
    if (existing) clearTimeout(existing);
    dismissedTicketTimeouts.delete(ticketId);
  },
  clear() {
    for (const timeout of dismissedTicketTimeouts.values()) clearTimeout(timeout);
    dismissedTicketTimeouts.clear();
  },
};

export const fetchKitchenBoardTickets = async (orgId: string): Promise<KitchenTicket[]> => {
  const tickets = await kitchenTicketsService.getActiveTickets(orgId);
  if (dismissedTicketTimeouts.size === 0) return tickets;
  return tickets.filter((ticket) => !dismissedTicketTimeouts.has(ticket.id));
};
